// @flow
import React from 'react';
import './CoachCardBack.scss';
import { Radio, Input, Button, Modal, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { CoachStatusTypesEnum } from '../../utilities/coachStatusTypesEnum';
import { ParticipantStatusTypesEnum } from '../../utilities/participantStatusTypesEnum';
import {
  patchCoach,
  releaseCoachParticipants,
} from '../../slices/coachesSlice';
import { fetchSchedule } from '../../slices/scheduleSlice';
import type { ScheduleCoach } from '../../slices/coachesSlice';
const { TextArea } = Input;

export default function CoachCardBack({
  coachSchedule,
  toggleSide,
  partnerData,
  day,
}: {
  coachSchedule: ScheduleCoach,
  toggleSide: any,
  coachId: any,
  partnerData: any,
  day: string,
}) {
  const daysMap = {
    M: 'Monday',
    Tu: 'Tuesday',
    W: 'Wednesday',
    Th: 'Thursday',
    F: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
  };
  const dispatch = useDispatch();
  const [note, setNote] = React.useState(coachSchedule.note);
  const [status, setStatus] = React.useState(() => {
    // 1. Get today's day of the week as a string (e.g., "Wednesday")
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    // work_days is nullable; a coach with none set must still open the card.
    const workDay = (coachSchedule.user.workDays ?? []).find(
      (day) => daysMap[day] === today
    );

    return workDay ? coachSchedule.status : CoachStatusTypesEnum.SCHEDULED_OFF;
  });
  const [showReleaseModal, setShowReleaseModal] = React.useState(false);
  const [releaseLoading, setReleaseLoading] = React.useState(false);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const participantsById = useSelector((state) => state.participants.byId);

  const onStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const onNoteChange = (e) => {
    setNote(e.target.value);
  };

  const close = (e) => {
    toggleSide();
  };

  const getScheduledCount = () => {
    return (coachSchedule.scheduledParticipants || []).filter(
      (id) =>
        participantsById[id]?.status === ParticipantStatusTypesEnum.SCHEDULED
    ).length;
  };

  const save = async (e) => {
    if (status === CoachStatusTypesEnum.CALLED_OUT && getScheduledCount() > 0) {
      dispatch(patchCoach({ id: coachSchedule.id, note, status }));
      setShowReleaseModal(true);
      return;
    }

    dispatch(patchCoach({ id: coachSchedule.id, note, status }));
    toggleSide();
  };

  const handleRelease = async () => {
    setReleaseLoading(true);
    try {
      await dispatch(releaseCoachParticipants(coachSchedule.id));
      if (!mountedRef.current) return;
      setShowReleaseModal(false);
      toggleSide();
      dispatch(fetchSchedule());
      message.success('Participants moved to participant center');
    } catch (err) {
      if (!mountedRef.current) return;
      message.error('Failed to release participants. Please try again.');
    } finally {
      if (mountedRef.current) {
        setReleaseLoading(false);
      }
    }
  };

  const handleKeepAssigned = () => {
    setShowReleaseModal(false);
    toggleSide();
  };

  let generateList = () => {
    return partnerData.map((item) => {
      return (
        <li key={item[0]}>
          <div className="name">{item[0]}</div>
          <div className="text">{item[1]}</div>
          <div className="text">{item[2]}</div>
          <br></br>
        </li>
      );
    });
  };

  return (
    <div className="coach-card-back">
      <h4>Attendance</h4>
      <div className="content">
        <Radio.Group onChange={onStatusChange} value={status}>
          <Radio value={CoachStatusTypesEnum.SCHEDULED}>Scheduled</Radio>
          <Radio value={CoachStatusTypesEnum.SCHEDULED_OFF}>
            Scheduled Off
          </Radio>
          <Radio value={CoachStatusTypesEnum.UNSCHEDULED}>Unscheduled</Radio>
          <Radio value={CoachStatusTypesEnum.CALLED_OUT}>Called Out</Radio>
        </Radio.Group>

        <div className="notes">
          <h4>Notes</h4>
          <TextArea rows={2} onChange={onNoteChange} value={note} />
        </div>

        <div className="footer">
          <Button className="cancel" onClick={close}>
            Cancel
          </Button>
          <Button className="ok" type="primary" onClick={save}>
            OK
          </Button>
        </div>
      </div>
      <h4>Partners</h4>
      <div className="partners-content">
        <ul className="partner-list">{generateList()}</ul>
      </div>

      <Modal
        title={`Coach ${coachSchedule.user.firstName} ${coachSchedule.user.lastName} has been marked as Called Out`}
        visible={showReleaseModal}
        onCancel={handleKeepAssigned}
        footer={[
          <Button key="keep" onClick={handleKeepAssigned}>
            Keep Assigned
          </Button>,
          <Button
            key="release"
            type="primary"
            loading={releaseLoading}
            onClick={handleRelease}
          >
            Move to Participant Center
          </Button>,
        ]}
      >
        <p>
          They have {getScheduledCount()} scheduled participant(s) today. Would
          you like to move them to the participant center so they can be
          reassigned?
        </p>
      </Modal>
    </div>
  );
}
